import React, { useState, useEffect } from 'react';
import { Camera, Pencil, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import BarcodeScannerModal from './BarcodeScannerModal';
import { confirmDialog } from "../ui/confirm-dialog";

const InventoryMachines = ({ axiosInstance, selectedWarehouse, warehouses = [] }) => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedCode, setScannedCode] = useState("");
    const [scannerMode, setScannerMode] = useState(""); // 'main_search'
    
    // State for Stock Machine Management
    const [machines, setMachines] = useState([]);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [isEditMachineOpen, setIsEditMachineOpen] = useState(false);

    // State for Brand Management
    const [brands, setBrands] = useState([]);

    // State for Machine Details Modal
    const [isMachineDetailsOpen, setIsMachineDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelMachines, setModelMachines] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchMachines();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchMachines = async () => {
        try {
            const response = await axiosInstance.get('/stock/machines');
            setMachines(response.data);
        } catch (error) {
            console.error("Error fetching machines:", error);
            toast.error("Failed to fetch machines");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleScanSuccess = (code) => {
        setScannedCode(code);
        if (scannerMode === 'main_search') {
            setSearchFilter(code);
        }
        setIsScannerOpen(false);
        setScannerMode("");
    };

    const openScanner = (mode) => {
        setScannerMode(mode);
        setIsScannerOpen(true);
    };

    const handleRowClick = (aggregatedMachine) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const relatedMachines = filteredMachines.filter(m => 
            normalize(m.model) === normalize(aggregatedMachine.model) && 
            normalize(m.brand) === normalize(aggregatedMachine.brand) &&
            normalize(m.itemDescription) === normalize(aggregatedMachine.itemDescription) &&
            normalize(m.itemCode) === normalize(aggregatedMachine.itemCode) &&
            (m.warehouse_id || 'null') === (aggregatedMachine.warehouse_id || 'null')
        );
        setSelectedModel(aggregatedMachine);
        setModelMachines(relatedMachines);
        setIsMachineDetailsOpen(true);
    };

    const handleEditMachine = (machine) => {
        setSelectedMachine(machine);
        setIsEditMachineOpen(true);
    };

    const handleDeleteMachine = async (machineId) => {
        const confirmed = await confirmDialog({
            title: "Delete Machine",
            description: "Are you sure you want to delete this machine?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/machines/${machineId}`);
            toast.success("Machine deleted successfully");
            
            // Refresh main list
            await fetchMachines();
            
            // Update modal list
            setModelMachines(prev => prev.filter(m => m.id !== machineId));
            
            // If no machines left for this model, close modal
            if (modelMachines.length <= 1) {
                setIsMachineDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting machine:", error);
            toast.error("Failed to delete machine");
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedMachine(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateMachine = async () => {
        if (!selectedMachine) return;
        
        try {
            const payload = {
                brand: selectedMachine.brand,
                model: selectedMachine.model,
                itemDescription: selectedMachine.itemDescription,
                itemCode: selectedMachine.itemCode,
                serialNumber: selectedMachine.serialNumber,
                quantity: parseInt(selectedMachine.quantity) || 0,
                warehouse_id: selectedMachine.warehouse_id || null
            };

            await axiosInstance.put(`/stock/machines/${selectedMachine.id}`, payload);
            toast.success("Machine updated successfully");
            setIsEditMachineOpen(false);
            setSelectedMachine(null);
            
            // Refresh main list
            await fetchMachines();
            
            // Update local state for details modal if it's open
            if (isMachineDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/machines');
                const allMachines = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelMachines = allMachines.filter(m => 
                    normalize(m.model) === normalize(selectedModel.model) && 
                    normalize(m.brand) === normalize(selectedModel.brand) &&
                    normalize(m.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(m.itemCode) === normalize(selectedModel.itemCode) &&
                    (m.warehouse_id || 'null') === (selectedModel.warehouse_id || 'null')
                );
                setModelMachines(updatedModelMachines);
            }
        } catch (error) {
            console.error("Error updating machine:", error);
            toast.error("Failed to update machine");
        }
    };

    // Filter and Sort Machines
    const filteredMachines = machines.filter(machine => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? machine.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (machine.itemCode?.toLowerCase().includes(searchLower) || 
             machine.serialNumber?.toLowerCase().includes(searchLower) ||
             machine.model?.toLowerCase().includes(searchLower) ||
             machine.itemDescription?.toLowerCase().includes(searchLower));
        
        // Warehouse Filter
        const matchesWarehouse = !selectedWarehouse || machine.warehouse_id === selectedWarehouse.id;

        return matchesBrand && matchesSearch && matchesWarehouse;
    });

    // Aggregate Machines by Brand + Model + Description + ItemCode + Warehouse
    const aggregatedMachines = Object.values(filteredMachines.reduce((acc, machine) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(machine.brand)}-${normalize(machine.model)}-${normalize(machine.itemDescription)}-${normalize(machine.itemCode)}-${machine.warehouse_id || 'null'}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...machine,
                quantity: 0,
                count: 0
            };
        }
        acc[key].quantity += (machine.quantity || 0); 
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedMachines = aggregatedMachines.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.quantity - a.quantity;
            case "lowest_qty":
                return a.quantity - b.quantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    const totalQuantity = sortedAggregatedMachines.reduce((sum, machine) => sum + machine.count, 0);

    const getWarehouseName = (id) => {
        if (!id) return "Unassigned";
        const wh = warehouses.find(w => w.id === id);
        return wh ? wh.name : "Unknown";
    };

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Top Controls Bar */}
            <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
                <div className="w-[200px] space-y-2">
                    <Label>Brand</Label>
                    <Select value={brandFilter} onValueChange={setBrandFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select Brand" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Brands</SelectItem>
                            {brands.map(brand => (
                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 space-y-2 min-w-[200px]">
                    <Label>Search</Label>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search Model, Description, Item Code, or Serial..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="flex-1"
                        />
                        <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => openScanner('main_search')}
                        >
                            <Camera className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div className="w-[200px] space-y-2">
                    <Label>Sort By</Label>
                    <Select value={sortOption} onValueChange={setSortOption}>
                        <SelectTrigger>
                            <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                            <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                            <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stock List Table */}
            <div className="bg-white p-4 rounded-lg shadow flex-1 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-orange-600 ml-1">{sortedAggregatedMachines.length}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-black font-bold ml-1">{totalQuantity}</span>
                    </div>
                </div>
                <div className="border rounded-md flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Warehouse</TableHead>
                                <TableHead className="font-bold">Brand</TableHead>
                                <TableHead>Model</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Item Code</TableHead>
                                <TableHead>Quantity</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedAggregatedMachines.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                                        No machines found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedAggregatedMachines.map((machine, index) => (
                                    <TableRow 
                                        key={`${machine.brand}-${machine.model}-${machine.warehouse_id}-${index}`} 
                                        className="hover:bg-green-100 cursor-pointer transition-colors"
                                        onClick={() => handleRowClick(machine)}
                                    >
                                        <TableCell className="text-gray-600">{getWarehouseName(machine.warehouse_id)}</TableCell>
                                        <TableCell className="font-bold">{machine.brand}</TableCell>
                                        <TableCell>{machine.model}</TableCell>
                                        <TableCell>{machine.itemDescription}</TableCell>
                                        <TableCell>{machine.itemCode}</TableCell>
                                        <TableCell>{machine.count}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Machine Details Modal */}
            <Dialog open={isMachineDetailsOpen} onOpenChange={setIsMachineDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Machine Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Total Quantity: {modelMachines.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Serial Number</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelMachines.map((machine) => (
                                    <TableRow key={machine.id}>
                                        {isSuperAdmin && <TableCell>{machine.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(machine.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{machine.brand}</TableCell>
                                        <TableCell>{machine.model}</TableCell>
                                        <TableCell>{machine.itemDescription}</TableCell>
                                        <TableCell>{machine.itemCode}</TableCell>
                                        <TableCell className="font-medium">{machine.serialNumber}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditMachine(machine)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                {isSuperAdmin && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => handleDeleteMachine(machine.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsMachineDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Machine Modal */}
            <Dialog open={isEditMachineOpen} onOpenChange={setIsEditMachineOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Machine</DialogTitle>
                        <DialogDescription>
                            Update the details of the machine stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedMachine && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedMachine.brand} 
                                        onValueChange={(value) => setSelectedMachine(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-warehouse" className="text-right">
                                    Warehouse
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedMachine.warehouse_id || "unassigned"} 
                                        onValueChange={(value) => setSelectedMachine(prev => ({ ...prev, warehouse_id: value === "unassigned" ? null : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Warehouse" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {warehouses.map(warehouse => (
                                                <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedMachine.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedMachine.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedMachine.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-serialNumber" className="text-right">
                                    Serial No.
                                </Label>
                                <div className="col-span-3 flex gap-2">
                                    <Input
                                        id="edit-serialNumber"
                                        name="serialNumber"
                                        value={selectedMachine.serialNumber}
                                        onChange={handleEditInputChange}
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedMachine.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateMachine}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <BarcodeScannerModal 
                isOpen={isScannerOpen} 
                onClose={() => setIsScannerOpen(false)} 
                onScanSuccess={handleScanSuccess} 
            />
        </div>
    );
};

export default InventoryMachines;
